import React, { useState, useCallback } from 'react';
import { Zap } from 'lucide-react';
import { ModelConfig } from '../../hooks/useModels';
import { useWorkflowForm } from '../../hooks/useWorkflowForm';
import { useFalGeneration } from '../../hooks/useFalGeneration';
import { LoadingSpinner } from '../LoadingSpinner';
import { DynamicFormFields } from './DynamicFormFields';
import { MobileStepperForm } from './MobileStepperForm';
import { GeneratingVideoModal } from '../GeneratingVideoModal';
import { MediaPreviewModal } from '../MediaPreviewModal';

interface DynamicWorkflowFormProps {
  model: ModelConfig;
  creditCost?: number;
  pageTitle?: string;
  pageDescription?: string;
  exampleMediaUrl?: string;
  exampleMediaType?: string;
  apiKeyId?: string;
  onCancel: () => void;
  onComplete?: () => void;
  onError?: (error: string) => void;
  appId?: string;
  onAssetsGenerated?: (assets: any[]) => void;
}

export const DynamicWorkflowForm: React.FC<DynamicWorkflowFormProps> = ({
  model,
  creditCost,
  pageTitle,
  pageDescription,
  exampleMediaUrl,
  exampleMediaType,
  apiKeyId,
  onCancel,
  onComplete,
  onError,
  appId,
  onAssetsGenerated
}) => {
  const [previewAsset, setPreviewAsset] = useState<any>(null);

  // Form state management
  const {
    formData,
    formErrors,
    handleInputChange, 
    resetForm,
    validateForm
  } = useWorkflowForm(model);

  // FAL generation hook
  const {
    isGenerating,
    generationProgress,
    generatedAssets,
    generationComplete,
    error: generationError,
    generateContent,
    creditsChecked,
    hasSufficientCredits,
  } = useFalGeneration(model, apiKeyId, appId);

  const handleSubmit = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();

    console.log('🆔 [FORM] Form submission with page app_id:', appId);
    console.log('📋 [FORM] Form data being submitted:', formData);

    // Don't allow submission if credits haven't been checked or are insufficient
    if (!creditsChecked || !hasSufficientCredits) {
      console.log('❌ [FORM] Cannot submit - insufficient credits or not checked');
      return;
    }

    console.log('🚀 [FORM] Starting form submission');

    // Validate form
    const validation = validateForm();
    if (!validation.isValid) {
      console.log('❌ [FORM] Validation failed:', validation.errors);
      return;
    }

    console.log('✅ [FORM] Validation passed, starting generation');

    // Start generation
    const result = await generateContent(formData);
    
    if (result.success) {
      console.log('🎉 [FORM] Generation completed successfully');
      if (result.assets && onAssetsGenerated) {
        onAssetsGenerated(result.assets);
      }
      onComplete?.();
    } else {
      console.log('❌ [FORM] Generation failed:', result.error);
      // Call the error handler to show ErrorModal
      if (onError && result.error) {
        onError(result.error);
      }
    }
  };
  
  // Reset form if a specific error message is displayed
  React.useEffect(() => {
    if (generationError) {
      // Don't reset form automatically, let ErrorModal handle it
      if (onError) {
        onError(generationError);
      }
    }
  }, [generationError, resetForm]);

  const handlePreviewAsset = (asset: any) => {
    setPreviewAsset(asset);
  };

  const handleDownloadAsset = (asset: any) => {
    const link = document.createElement('a');
    link.href = asset.public_url;
    link.download = asset.filename || `generated-${asset.id}`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const onRestartForm = useCallback(() => {
    // Reload the entire URL for a fresh start
    window.location.reload();
  }, []);

  // Mobile layout (full screen stepper)
  const isMobile = window.innerWidth < 1024;
  
  if (isMobile) {
    return (
      <>
        <MobileStepperForm
          fields={model.input_schema || []}
          values={formData}
          onChange={handleInputChange}
          onSubmit={handleSubmit}
          onCancel={onCancel}
          isSubmitting={isGenerating}
          submitLabel="Generate"
          creditCost={creditCost || model.credit_cost}
          disabled={isGenerating}
          creditsChecked={creditsChecked}
          hasSufficientCredits={hasSufficientCredits}
          pageTitle={pageTitle}
          pageDescription={pageDescription}
          exampleMediaUrl={exampleMediaUrl}
          exampleMediaType={exampleMediaType}
          generatedAssets={generatedAssets}
          onPreviewAsset={handlePreviewAsset}
          showSuccessStep={generationComplete}
          showGeneratingModal={false}
          onGeneratingComplete={() => console.log('Generation completed')}
          mediaType={model.output_type} // Pass mediaType to modal
          onRestartForm={onRestartForm}
          onError={onError}
          appId={appId}
        />

        {/* Generating Modal for Mobile */}
        {isGenerating && (
          <GeneratingVideoModal 
            isOpen={isGenerating}
            isGenerating={isGenerating}
            onComplete={() => {
              // This will be called when generation is complete
            }}
            mediaType={model.output_type}
          />
        )}

        {/* Media Preview Modal */}
        {previewAsset && (
          <MediaPreviewModal
            isOpen={!!previewAsset}
            onClose={() => setPreviewAsset(null)}
            asset={previewAsset}
            onDownload={() => handleDownloadAsset(previewAsset)}
          />
        )}
      </>
    );
  }

  // Desktop layout
  return (
    <>
      {/* Generating Modal for Desktop */}
      {isGenerating && (
        <GeneratingVideoModal 
          isOpen={isGenerating}
          isGenerating={isGenerating}
          onComplete={() => {
            // This will be called when generation is complete
          }}
          mediaType={model.output_type}
        />
      )}

      <div className="h-screen flex flex-col overflow-hidden">
        {/* Main Content */}
        <div className="flex-1 flex flex-col min-h-0">
          <div className="flex-1 overflow-y-auto">
            <div className="max-w-2xl mx-auto p-6">
              {/* Page Description */}
              {pageDescription && (
                <div className="mb-6 text-center">
                  <p className="text-th-secondary">{pageDescription}</p>
                </div>
              )}

              {/* Form */}
              <form onSubmit={handleSubmit} className="space-y-6 pb-32">
                <DynamicFormFields
                  fields={model.input_schema || []}
                  values={formData}
                  onChange={handleInputChange}
                  errors={formErrors}
                  isAdvancedMode={false}
                  disabled={isGenerating}
                  appId={appId}
                />

                {/* Generation Error */}

                {/* Generation Progress */}
                {isGenerating && generationProgress && (
                  <div className="p-4 bg-brand/10 border border-brand/30 rounded-xl">
                    <div className="flex items-center space-x-2">
                      <LoadingSpinner size="sm" />
                      <span className="text-brand font-medium">Generating...</span>
                    </div>
                    <p className="text-brand/70 text-sm mt-1">{generationProgress}</p>
                  </div>
                )}
              </form>
            </div>
          </div>

          {/* Submit Button - Sticky to bottom */}
          <div className="flex-shrink-0 bg-surface border-t border-subtle p-6 z-10">
            {/* AI Disclaimer */}
            <div className="mb-4 text-center">
              <p className="text-xs text-th-secondary/70">
                AI may generate inaccurate results. Credits are non-refundable.
              </p>
            </div>

            <button
              onClick={handleSubmit}
              disabled={isGenerating || !creditsChecked || !hasSufficientCredits}
              className="w-full flex items-center justify-center space-x-2 px-6 py-4 bg-brand text-surface rounded-xl font-semibold text-lg hover:bg-brand/90 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-lg shadow-brand/30"
            >
              {isGenerating ? (
                <>
                  <LoadingSpinner size="sm" className="text-white" />
                  <span>Generating...</span>
                </>
              ) : !creditsChecked ? (
                <>
                  <LoadingSpinner size="sm" className="text-white" />
                  <span>Checking Credits...</span>
                </>
              ) : !hasSufficientCredits ? (
                <>
                  <span>Insufficient Credits</span>
                </>
              ) : (
                <>
                  <Zap className="w-5 h-5" />
                  <span>Generate ({creditCost || model.credit_cost} credits)</span>
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Media Preview Modal */}
      {previewAsset && (
        <MediaPreviewModal
          isOpen={!!previewAsset}
          onClose={() => setPreviewAsset(null)}
          asset={previewAsset}
          onDownload={() => handleDownloadAsset(previewAsset)}
        />
      )}
    </>
  );
};